// React Router v6 wiring with Framer Motion fade-up page transitions.

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { BottomNav, SkipLink } from "@/design-system";
import { ROUTES } from "@/lib/paths";
import { AdultGateProvider } from "@/lib/AdultGateProvider";
import { useAdultGate } from "@/lib/adultGateContext";
import { useAuth } from "@/stores/auth";
import { Account } from "@/screens/Account";
import { AdultGate } from "@/screens/AdultGate";
import { Chat } from "@/screens/Chat";
import { Landing } from "@/screens/Landing";
import { NotFound } from "@/screens/NotFound";
import { Recommendation } from "@/screens/Recommendation";
import { CheckEmail } from "@/screens/auth/CheckEmail";
import { ForgotPassword } from "@/screens/auth/ForgotPassword";
import { Login } from "@/screens/auth/Login";
import { ResetPassword } from "@/screens/auth/ResetPassword";
import { Signup } from "@/screens/auth/Signup";
import { VerifyEmail } from "@/screens/auth/VerifyEmail";

// ─── Route wrappers ───────────────────────────────────────────

function LandingRoute() {
  const navigate = useNavigate();
  return (
    <Landing
      onStart={() => navigate(ROUTES.signup)}
      onLogin={() => navigate(ROUTES.login)}
    />
  );
}

function SignupRoute() {
  const navigate = useNavigate();
  return (
    <Signup
      onBack={() => navigate(ROUTES.landing)}
      onLogin={() => navigate(ROUTES.login)}
      onSignedUp={(email) =>
        navigate(`${ROUTES.checkEmail}?email=${encodeURIComponent(email)}`)
      }
    />
  );
}

function LoginRoute() {
  const navigate = useNavigate();
  return (
    <Login
      onBack={() => navigate(ROUTES.landing)}
      onSignup={() => navigate(ROUTES.signup)}
      onForgotPassword={() => navigate(ROUTES.forgotPassword)}
      onLoggedIn={() => navigate(ROUTES.chat)}
    />
  );
}

function CheckEmailRoute() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  return (
    <CheckEmail
      email={params.get("email")}
      onLogin={() => navigate(ROUTES.login)}
    />
  );
}

function VerifyEmailRoute() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  return (
    <VerifyEmail
      token={params.get("token")}
      onLogin={() => navigate(ROUTES.login)}
      onHome={() => navigate(ROUTES.landing)}
    />
  );
}

function ForgotPasswordRoute() {
  const navigate = useNavigate();
  return (
    <ForgotPassword
      onBack={() => navigate(ROUTES.login)}
      onLogin={() => navigate(ROUTES.login)}
    />
  );
}

function ResetPasswordRoute() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  return (
    <ResetPassword
      token={params.get("token")}
      onLogin={() => navigate(ROUTES.login)}
      onHome={() => navigate(ROUTES.landing)}
    />
  );
}

function ChatRoute() {
  const navigate = useNavigate();
  // ICSP-43: legacy accounts must self-certify as adult before the chat
  // mounts; new signups already declared and pass straight through.
  return (
    <AdultGate>
      <Chat
        onBack={() => navigate(ROUTES.account)}
        onComplete={() => navigate(ROUTES.recommendation)}
      />
    </AdultGate>
  );
}

function RecommendationRoute() {
  const navigate = useNavigate();
  return (
    <Recommendation
      onBack={() => navigate(ROUTES.chat)}
      onSubscribe={() => navigate(ROUTES.account)}
    />
  );
}

function AccountRoute() {
  const navigate = useNavigate();
  return (
    <Account
      onBack={() => navigate(ROUTES.recommendation)}
      onSeeRecommendation={() => navigate(ROUTES.recommendation)}
      onStartChat={() => navigate(ROUTES.chat)}
    />
  );
}

function NotFoundRoute() {
  const navigate = useNavigate();
  return <NotFound onHome={() => navigate(ROUTES.landing)} />;
}

// ─── Auth guard ───────────────────────────────────────────────

function RequireAuth() {
  const token = useAuth((s) => s.token);
  const location = useLocation();
  if (!token) {
    return (
      <Navigate
        to={ROUTES.login}
        replace
        state={{ from: location.pathname }}
      />
    );
  }
  return <Outlet />;
}

// ─── Transitions + persistent chrome ──────────────────────────

// ICSP-59: routes that carry the persistent bottom nav. Allow-list (not a
// denylist) so landing/auth/checkemail/verify/404 stay bar-less by default.
const NAV_PATHS = new Set<string>([
  ROUTES.chat,
  ROUTES.recommendation,
  ROUTES.account,
]);

function AnimatedLayout() {
  const location = useLocation();
  const reduce = useReducedMotion();
  // ICSP-59: bar shows only on the authed screens, and not while the
  // AdultGate interstitial is up (it publishes the flag from /chat).
  const adultGated = useAdultGate();
  const showNav = NAV_PATHS.has(location.pathname) && !adultGated;

  // The bar is an in-flow `shrink-0` column sibling (NOT position:fixed), so
  // it reserves its own space and the screen region is bounded above it.
  // `min-h-0` keeps the flex screens' internal scroll working.
  return (
    <div className="flex h-full flex-col">
      <SkipLink />
      <div className="min-h-0 flex-1">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={location.pathname}
            initial={reduce ? false : { opacity: 0, y: 6 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -4 }}
            transition={{ duration: 0.24, ease: [0.2, 0.7, 0.3, 1] }}
            className="h-full"
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </div>
      {showNav && <BottomNav />}
    </div>
  );
}

// ─── Router ───────────────────────────────────────────────────

const router = createBrowserRouter([
  {
    element: <AnimatedLayout />,
    children: [
      { path: ROUTES.landing, element: <LandingRoute /> },
      { path: ROUTES.signup, element: <SignupRoute /> },
      { path: ROUTES.login, element: <LoginRoute /> },
      { path: ROUTES.checkEmail, element: <CheckEmailRoute /> },
      { path: ROUTES.verifyEmail, element: <VerifyEmailRoute /> },
      { path: ROUTES.forgotPassword, element: <ForgotPasswordRoute /> },
      { path: ROUTES.resetPassword, element: <ResetPasswordRoute /> },
      {
        element: <RequireAuth />,
        children: [
          { path: ROUTES.chat, element: <ChatRoute /> },
          { path: ROUTES.recommendation, element: <RecommendationRoute /> },
          { path: ROUTES.account, element: <AccountRoute /> },
        ],
      },
      { path: "*", element: <NotFoundRoute /> },
    ],
  },
]);

export function AppRouter() {
  // ICSP-59: provider sits above the router so AnimatedLayout (reader) and
  // AdultGate (writer) share the nav-visibility flag.
  return (
    <AdultGateProvider>
      <RouterProvider router={router} />
    </AdultGateProvider>
  );
}
